// src/config/roles.js
//
// Mirror of Backend/src/config/roles.js for labels, ordering and client-side
// gating. The server remains the authority; this only hides actions the user
// cannot perform so they don't hit avoidable 403s.

export const ROLES = Object.freeze({
  OWNER: "owner",
  ADMIN: "admin",
  MANAGER: "manager",
  PARTNER: "partner",
  CALLER: "caller",
});

export const CAPABILITIES = Object.freeze({
  LEADS_VIEW_ALL: "leads.view_all",
  LEADS_VIEW_OWN: "leads.view_own",
  LEADS_UPDATE: "leads.update",
  LEADS_ASSIGN: "leads.assign",
  LEADS_EXPORT: "leads.export",
  USERS_VIEW: "users.view",
  USERS_INVITE: "users.invite",
  USERS_REMOVE: "users.remove",
  USERS_CHANGE_ROLE: "users.change_role",
  USERS_RESET_PASSWORD: "users.reset_password",
  COMPANY_EDIT: "company.edit",
  BILLING_MANAGE: "billing.manage",
  AUDIT_VIEW: "audit.view",
});

export const ROLE_DEFINITIONS = Object.freeze({
  [ROLES.OWNER]: {
    label: "Owner",
    rank: 50,
    description:
      "Full control of the organisation, including billing, people and every lead.",
    capabilities: [
      CAPABILITIES.LEADS_VIEW_ALL,
      CAPABILITIES.LEADS_VIEW_OWN,
      CAPABILITIES.LEADS_UPDATE,
      CAPABILITIES.LEADS_ASSIGN,
      CAPABILITIES.LEADS_EXPORT,
      CAPABILITIES.USERS_VIEW,
      CAPABILITIES.USERS_INVITE,
      CAPABILITIES.USERS_REMOVE,
      CAPABILITIES.USERS_CHANGE_ROLE,
      CAPABILITIES.USERS_RESET_PASSWORD,
      CAPABILITIES.COMPANY_EDIT,
      CAPABILITIES.BILLING_MANAGE,
      CAPABILITIES.AUDIT_VIEW,
    ],
  },
  [ROLES.ADMIN]: {
    label: "Admin",
    rank: 40,
    description:
      "Manages people, roles, company settings and the full lead book. Cannot manage owners or billing.",
    capabilities: [
      CAPABILITIES.LEADS_VIEW_ALL,
      CAPABILITIES.LEADS_VIEW_OWN,
      CAPABILITIES.LEADS_UPDATE,
      CAPABILITIES.LEADS_ASSIGN,
      CAPABILITIES.LEADS_EXPORT,
      CAPABILITIES.USERS_VIEW,
      CAPABILITIES.USERS_INVITE,
      CAPABILITIES.USERS_REMOVE,
      CAPABILITIES.USERS_CHANGE_ROLE,
      CAPABILITIES.USERS_RESET_PASSWORD,
      CAPABILITIES.COMPANY_EDIT,
      CAPABILITIES.AUDIT_VIEW,
    ],
  },
  [ROLES.MANAGER]: {
    label: "Manager",
    rank: 30,
    description:
      "Works the whole lead book, assigns leads and updates any lead. No people or billing access.",
    capabilities: [
      CAPABILITIES.LEADS_VIEW_ALL,
      CAPABILITIES.LEADS_VIEW_OWN,
      CAPABILITIES.LEADS_UPDATE,
      CAPABILITIES.LEADS_ASSIGN,
      CAPABILITIES.LEADS_EXPORT,
      CAPABILITIES.USERS_VIEW,
    ],
  },
  [ROLES.PARTNER]: {
    label: "Partner",
    rank: 20,
    description:
      "External partner organisation user. Sees and updates only leads assigned to them.",
    capabilities: [CAPABILITIES.LEADS_VIEW_OWN, CAPABILITIES.LEADS_UPDATE],
  },
  [ROLES.CALLER]: {
    label: "Caller",
    rank: 10,
    description: "Calling user. Sees and updates only leads assigned to them.",
    capabilities: [CAPABILITIES.LEADS_VIEW_OWN, CAPABILITIES.LEADS_UPDATE],
  },
});

const ROLE_ALIASES = {
  user: ROLES.CALLER,
  member: ROLES.CALLER,
  staff: ROLES.CALLER,
  sales: ROLES.CALLER,
  agent: ROLES.CALLER,
};

export function normaliseRole(role) {
  const raw = String(role || "").trim().toLowerCase();
  if (!raw) return ROLES.CALLER;
  const mapped = ROLE_ALIASES[raw] || raw;
  return ROLE_DEFINITIONS[mapped] ? mapped : ROLES.CALLER;
}

export function getRoleDefinition(role) {
  return ROLE_DEFINITIONS[normaliseRole(role)];
}

export function roleLabel(role) {
  return getRoleDefinition(role).label;
}

export function getCapabilities(role) {
  return new Set(getRoleDefinition(role).capabilities);
}

export function hasCapability(userOrRole, capability) {
  if (!userOrRole) return false;
  if (typeof userOrRole === "object" && userOrRole.isSuperAdmin) return true;
  if (
    typeof userOrRole === "object" &&
    Array.isArray(userOrRole.capabilities) &&
    userOrRole.capabilities.length
  ) {
    return userOrRole.capabilities.includes(capability);
  }
  const role = typeof userOrRole === "object" ? userOrRole.role : userOrRole;
  return getCapabilities(role).has(capability);
}

export function assignableRoles(actor) {
  if (actor?.isSuperAdmin) {
    return [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER, ROLES.PARTNER, ROLES.CALLER];
  }
  const actorRank = getRoleDefinition(actor?.role).rank;
  return Object.entries(ROLE_DEFINITIONS)
    .filter(
      ([role, definition]) =>
        role !== ROLES.OWNER && definition.rank <= actorRank,
    )
    .map(([role]) => role);
}

export function canManageUser(actor, target) {
  if (!actor || !target) return false;
  if (actor.isSuperAdmin) return true;
  if (actor.id && target.id && Number(actor.id) === Number(target.id)) {
    return false;
  }
  const actorRole = normaliseRole(actor.role);
  if (![ROLES.OWNER, ROLES.ADMIN].includes(actorRole)) return false;
  return normaliseRole(target.role) !== ROLES.OWNER;
}

export function canSeeAllLeads(user) {
  return hasCapability(user, CAPABILITIES.LEADS_VIEW_ALL);
}

export const ROLE_OPTIONS = Object.entries(ROLE_DEFINITIONS).map(
  ([value, definition]) => ({
    value,
    label: definition.label,
    description: definition.description,
  }),
);
